import { useEffect, useState } from 'react';
import { initMercadoPago, Payment, StatusScreen } from '@mercadopago/sdk-react';
import logoMP from "../../images/mercado-pago-logo.png";
import './CheckoutModal.css';
import io from "socket.io-client";
import { auth } from '../../../contexts/authentication';
import { getUser, UserData } from '../getUserDB';
import { createWorkspace } from '../../utils/typebot/fetchTypebot';
import CircularWithValueLabel from '../../components/CircularLoader';
import { useMediaQuery, useTheme } from '@mui/material';
const PUBLIC_KEY = import.meta.env.VITE_MERCADO_PAGO_PUBLIC_KEY;
const baseUrl = import.meta.env.VITE_API_BASE_URL;

interface CheckoutModalProps {
  onClose: () => void;
  item: Object,
  plan: string,
}

const CheckoutModal = ({ onClose, item, plan }: CheckoutModalProps) => {
  const currentUser = auth.currentUser;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [userDB, setUserDB] = useState<UserData>();
  const [correctDate, setCorrectDate] = useState<Date | null>(null);
  const [preferenceId, setPreferenceId] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [externalRef, setExternalRef] = useState<string | null>(null);
  const [productPrice, setProductPrice] = useState<Number | null>(null);
  const [productDescription, setProductDescription] = useState<string | null>(null);
  const [showCheckout, setShowCheckout] = useState(true);
  const [nextMonth, setNextMonth] = useState<string | null>(null);
  const [nextSemester, setNextSemester] = useState<string | null>(null);
  const [nextYear, setNextYear] = useState<string | null>(null);

  useEffect(() => {
    const dataUser = async () => {
      const data = await getUser();
      setUserDB(data);
    }
    dataUser();
  }, []);

  useEffect(() => {
    if (correctDate) {
      const formatUTCToBr = (date: Date) => {
        const dia = String(date.getUTCDate()).padStart(2, '0');
        const mes = String(date.getUTCMonth() + 1).padStart(2, '0');
        const ano = date.getUTCFullYear();
        return `${dia}/${mes}/${ano}`;
      };

      const nextMonthDate = new Date(correctDate);
      nextMonthDate.setUTCMonth(correctDate.getUTCMonth() + 1);
      setNextMonth(formatUTCToBr(nextMonthDate));

      const nextSemesterDate = new Date(correctDate);
      nextSemesterDate.setUTCMonth(correctDate.getUTCMonth() + 6);
      setNextSemester(formatUTCToBr(nextSemesterDate));

      const nextYearDate = new Date(correctDate);
      nextYearDate.setUTCMonth(correctDate.getUTCMonth() + 12);
      setNextYear(formatUTCToBr(nextYearDate));
    }
  }, [correctDate]);

  useEffect(() => {
    const fetchUserPlan = async () => {
      const todayUTC = new Date();
      try {
        if (userDB && userDB.vencimento) {
          const [diaVenc, mesVenc, anoVenc] = userDB.vencimento.split('/').map(Number);
          const vencimentoDate = new Date(anoVenc, mesVenc - 1, diaVenc);

          if (vencimentoDate > todayUTC) {
            setCorrectDate(vencimentoDate);
          } else {
            setCorrectDate(todayUTC);
          }
        } else {
          setCorrectDate(todayUTC);
        }
      } catch (error) {
        console.error("Erro ao buscar o plano:", error);
      }
    }
    fetchUserPlan();
  }, [userDB]);

  // Inicializa o Mercado Pago
  useEffect(() => {
    initMercadoPago(PUBLIC_KEY, {
      locale: 'pt-BR',
    });

    const fetchPreference = async () => {
      try {
        console.log(baseUrl);

        const response = await fetch(`${baseUrl}/api/create_preference`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            payerInfo: {
              name: currentUser?.displayName,
              email: currentUser?.email
            },
            item,
          })
        });

        if (!response.ok) {
          throw new Error("Erro ao criar a preferência de pagamento");
        }

        const data = await response.json();
        setPreferenceId(data.id); // Define o ID da preferência
        setExternalRef(data.external_reference);
        setProductPrice(data.items[0].unit_price);
        setProductDescription(data.items[0].description);
      } catch (error) {
        console.error("Erro ao buscar a preferência:", error);
      }
    };

    fetchPreference();
  }, []);

  useEffect(() => {
    const updateConnectState = () => {
      const socket = io(baseUrl, {
        query: { externalRef: externalRef },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 10000
      });
      try {
        socket.on('connect', () => {
          socket.emit('join-room', externalRef);
        });
        socket.on('payment-details', (status: string) => {
          console.log('Status do pagamento:', status);
          if (status === 'approved' && userDB) {
            createWorkspace(userDB.email);
            // Envia mensagem para cliente após a compra via endpoint
            fetch(`${baseUrl}/send-message`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name: userDB.name,
                whatsapp: userDB.whatsapp
              })
            }).catch(error => {
              console.error("Erro ao enviar mensagem:", error);
            });

            // Envia mensagem para Mylher após a compra via endpoint
            fetch(`${baseUrl}/send-message-to-owner`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                mensagem: `Novo cliente *Nextalk* 💰🚀\n\n*Nome*: ${userDB.name}\n*Email*: ${userDB.email}`,
              })
            }).catch(error => {
              console.error("Erro ao enviar mensagem:", error);
            });
          };

          // Atualiza o colunas status e data do pagamento no banco de dados
          const updateUserDB = async () => {
            console.log('Atualizando User no dbsalepage...');
            try {
              const response = await fetch("/update-user", {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  user: {
                    ...userDB,
                    status: status === 'approved' ? 'ativo' : 'inativo',
                    plano_atual: plan,
                    vencimento:
                      plan === 'Flex' ? nextMonth
                        : plan === 'Smart' ? nextSemester
                          : nextYear,
                  },
                }),
              });
              const data = await response.json();
              setUserDB(data.user);

              if (response.ok) {
                console.log('User atualizado com sucesso!');
              } else {
                console.error('Erro ao atualizar o User no dbsalepage');
              }
            } catch (error) {
              console.error("Erro ao atualizar o User:", error);
            }
          }
          updateUserDB();
        });
        socket.on('disconnect', () => {
        });
      } catch (error) {
        console.error('Erro ao atualizar estado de conexão:', error);
      }
      return () => {
        socket.disconnect();
      };
    }
    updateConnectState();
    // }, [externalRef, paymentStatus]);
  }, [externalRef]);

  if (!preferenceId) {
    return (
      <div className="modal-overlay">
        <div className="modal-content-checkout" style={{ display: 'flex', justifyContent: 'center' }}>
          <button className="modal-close-checkout" onClick={onClose}>X</button>
          <CircularWithValueLabel />
        </div>
      </div>
    )
  }

  const initialization = {
    amount: productPrice,
    preferenceId, // Usa o ID da preferência gerado pelo backend
    external_reference: externalRef,
  };

  const customization = {
    paymentMethods: {
      bankTransfer: ["all"],
      creditCard: ["all"],
      maxInstallments: plan === "Flex" ? 1 : plan === "Smart" ? 6 : 12,
      // maxInstallmentsNoInterest: 12,
    },
    visual: {
      showExternalReference: true,
    },
  };

  // Lida com o envio do pagamento
  const onSubmit = async ({ formData }: any) => {
    console.log(formData);

    try {
      formData.description = productDescription;
      formData.transaction_amount = initialization.amount;
      formData.payment_method_id;
      formData.payer;
      formData.external_reference = initialization.external_reference;
      formData.token;
      formData.firstName = userDB?.name;
      const response = await fetch(`${baseUrl}/v1/payments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData), // Dados enviados ao backend
      });
      const result = await response.json();
      setPaymentId(result.payment.id);
      setShowCheckout(false);
    } catch (error) {
      console.error("Erro ao processar o pagamento:", error);
    }
  };

  const onError = async (error: any) => {
    console.error("Erro no Brick:", error);
    alert("Erro ao carregar o método de pagamento.");
  };

  const onReady = async () => {
    console.log("Brick carregado com sucesso!");
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-checkout"
        style={isDesktop ? {} : {
          maxHeight: '80vh',
          marginTop: '60px',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button className="modal-close-checkout" onClick={onClose}>X</button>
          <img
            src={logoMP}
            alt="logo-mercado-pago"
            style={{
              padding: '20px',
              width: '300px',
            }}
          />
        </div>
        {showCheckout ? (
          <Payment
            initialization={initialization as any}
            customization={customization as any}
            onSubmit={onSubmit}
            onReady={onReady}
            onError={onError}
          />
        ) : (
          paymentId ? (
            <StatusScreen
              initialization={{ paymentId }}
              customization={customization}
              onError={onError}
              onReady={onReady}
            />
          ) : (
            <div className="d-flex align-items-center">
              <strong role="status">Carregando status do pagamento...</strong>
              <div className="spinner-border ms-auto" aria-hidden="true"></div>
            </div>
          )
        )}
      </div>
    </div>
  )
};

export default CheckoutModal;